/**
 * Shared descriptor mapping core.
 *
 * Holds the version-agnostic programme-to-template field mapping plus the shared
 * services (schedule builder usage, strand/assessment/embedded helpers) consumed
 * by the variant-specific descriptor mappers (the `non-cas` mapper today, the
 * `cas` mapper alongside it). {@link buildDescriptorCore} produces the canonical
 * {@link DescriptorData}; variant mappers consume it and apply only their
 * variant-specific field overrides.
 *
 * This is a **pure function** — it has no side effects and depends only on
 * the Programme type. When the template gains new tags, add the corresponding
 * fields here and in the DescriptorData type.
 *
 * @module export/descriptor-core
 */

import type {
  AwardStandard,
  CasAwardsData,
  EmbeddedAward,
  Module,
  ModuleAssessment,
  PLO,
  Programme,
  ProgrammeVersion,
  ProviderContact,
  ResourceRequirement,
  StaffProfile,
  TransferProgressionArrangement,
  TransferProgressionArrangementType,
} from "@/types";
import { type AppendixStrandId, resolveAppendixStrand } from "@/utils/appendix-mapping";
import { projectProgrammeAssessmentVersion } from "@/utils/assessment-overrides";
import {
  assessmentRequirementLabels,
  classifyAssessmentType,
  formatAssessmentRequirements,
} from "@/utils/assessments";
import {
  type AwardCreditStatus,
  getAwardCreditProjection,
  getProgrammePathwayProjection,
} from "@/utils/award-pathways";
import { exitAwardsByStage, exitAwardTitle } from "@/utils/award-pathways";
import { isFeAwardType } from "@/utils/award-types";
import { getElectiveOptionSelection } from "@/utils/electives";
import { defaultPatternFor, normalizeDeliveryModality, resolveEffortHours } from "@/utils/helpers";
import { getStandardIndicators } from "@/utils/migrate-programme";
import { versionDurationYears } from "@/utils/programme-duration";
import { moduleTeachingWeeks, versionTeachingWeeks } from "@/utils/teaching-weeks";

import {
  buildScheduleData,
  type ScheduleData,
  type ScheduleModuleRow,
  type ScheduleStageData,
} from "./schedule-data";

export const NFQ_GRID_STANDARD_ID = "nfq-grid-level-indicators";

// ============================================================================
// Output Types
// ============================================================================

/** Programme outcome data for the Section 2.4 MIPLO loop. */
export interface DescriptorMiploEntry {
  code: string;
  text: string;
}

export interface DescriptorTransferProgressionArrangement {
  programmeName: string;
  provider: string;
}

export interface DescriptorStaffProfile {
  role: string;
  qualificationsExperience: string;
  numberRequired: string;
  inPost: string;
  toBeRecruited: string;
}

export interface DescriptorResourceRequirement {
  resource: string;
  quantity: string;
  inPlace: string;
  toBeProcured: string;
}

/** MIMLO data for the {FOR mimlo IN module.mimlos} loop. */
export interface DescriptorMimlo {
  index: number;
  text: string;
  relatedPlos: string;
  /** CAS component award code this outcome maps to (CAS programmes only). */
  componentCode?: string;
  /** Matching CAS component learning-outcome reference, e.g. "LO 3" (CAS programmes only). */
  componentLo?: string;
}

/** Assessment data for the {FOR asmt IN module.assessments} loop. */
export interface DescriptorAssessment {
  mimloText: string;
  title: string;
  type: string;
  /** Human-readable technique retaining both the type and identifying title. */
  technique: string;
  /** Author's notes on the assessment (e.g. design rationale or provenance). */
  notes: string;
  /** Capstone, hurdle, mandatory-pass, and threshold rules. */
  requirements: string;
  weighting: number;
}

/** Module data for the {FOR module IN modules} loop. */
export interface DescriptorModule {
  code: string;
  title: string;
  stage: string;
  semester: string;
  credits: number;
  mandatoryElective: string;
  durationWeeks: string;
  hoursPerWeek: string;

  // Effort hours
  effortOnsite: string;
  effortSyncOnline: string;
  effortSyncHybrid: string;
  effortAsync: string;
  effortIndependent: string;
  effortWorkBased: string;
  effortOther: string;
  effortOtherDescription: string;
  effortTotal: string;
  /** True when the module has no recorded effort hours (all values zero). */
  effortIncomplete: boolean;

  // Assessment percentages
  assessContinuous: string;
  assessExamInPerson: string;
  assessPractical: string;
  assessProject: string;
  assessExamOnline: string;
  assessWorkBased: string;

  // Nested arrays
  mimlos: DescriptorMimlo[];
  assessments: DescriptorAssessment[];
  readingListText: string;
  synopsis: string;
  indicativeContent: string;
  prerequisites: string;
  coRequisites: string;
  resourcesRequired: string;
  maximumLearners: string;
  staffQualifications: string;
  /** CAS component awards achievable on this module (CAS programmes only). */
  casComponents?: { code: string; title: string }[];
}

/**
 * A descriptor section describing an award embedded within the programme.
 * First-class awards are preferred; the legacy embedded programme is a fallback.
 */
export interface DescriptorEmbeddedSection {
  title: string;
  awardType: string;
  nfqLevel: string;
  ects: string;
  equivalentEctsValue: boolean;
  exitOnly: boolean;
}

export interface DescriptorPathwayModule {
  code: string;
  title: string;
  credits: number;
}

export interface DescriptorAwardPathway {
  id: string;
  title: string;
  awardType: string;
  relationship: string;
  nfqLevel: string;
  credits: number;
  casComponentCode: string;
  versionLabels: string[];
  stageLabels: string[];
  modules: DescriptorPathwayModule[];
  minimumCredits: number;
  maximumCredits: number;
  creditStatus: AwardCreditStatus;
  notes: string;
}

export interface DescriptorElectiveOption {
  id: string;
  name: string;
  code: string;
  selectionRule: string;
  requiredModuleCount: number;
  availableModuleCount: number;
  availableModules: DescriptorPathwayModule[];
  representativeModules: DescriptorPathwayModule[];
  representativeCredits: number;
  isRepresentative: boolean;
  complete: boolean;
}

export interface DescriptorElectiveChoiceSlot {
  id: string;
  name: string;
  code: string;
  requiredCredits: number;
  representativeOptionName: string;
  representativeCredits: number;
  complete: boolean;
  options: DescriptorElectiveOption[];
}

export interface DescriptorRepresentativePathway {
  modules: DescriptorPathwayModule[];
  declaredCredits: number;
  projectedCredits: number;
  creditStatus: AwardCreditStatus;
  incompleteChoiceSlots: string[];
}

/**
 * Descriptor §6.1 Stage Level Outline entry. One entry per stage across every
 * programme version, summarising the stage's identity, credit target, module
 * count, and any embedded exit award (PD-080).
 */
export interface DescriptorStageOutline {
  versionLabel: string;
  stageName: string;
  sequence: number;
  creditsTarget: number;
  moduleCount: number;
  hasExitAward: boolean;
  exitAwardTitle: string;
}

/**
 * Descriptor §6.2 Module Level Outline entry. One entry per module, summarising
 * the module's identity (code, title), credit value, mandatory/elective status,
 * stage placement, and the count of its learning outcomes and assessments
 * (PD-081).
 */
export interface DescriptorModuleOutline {
  code: string;
  title: string;
  credits: number;
  mandatoryElective: string;
  stage: string;
  semester: string;
  /** "M, Stage 1, Semester 2", leaving out a stage or semester that is unset. */
  placement: string;
  mimloCount: number;
  assessmentCount: number;
  /**
   * Short module narrative rendered in §6.2 where the source module carries one
   * (`synopsis` / `description`). The schema has no dedicated synopsis field, so
   * an absent narrative is a genuine blank ("") rather than invented content.
   */
  synopsis?: string;
}

/** Module-outline entries grouped by their owning version and stage for §6.2. */
export interface DescriptorModuleOutlineStage {
  versionLabel: string;
  stageName: string;
  modules: DescriptorModuleOutline[];
}

/**
 * A single assessment placed in the descriptor §6.9 Indicative Assessment
 * Calendar, carrying its identifying detail and the owning module (PD-082).
 */
export interface DescriptorAssessmentCalendarEntry {
  moduleCode: string;
  moduleTitle: string;
  title: string;
  type: string;
  weighting: number;
}

/**
 * Descriptor §6.9 Indicative Assessment Calendar row. One entry per indicative
 * week that carries at least one assessment, listing every assessment whose
 * `indicativeWeek` matches that week. Rows are ordered by ascending week
 * (PD-082).
 */
export interface DescriptorAssessmentCalendarRow {
  week: number;
  assessments: DescriptorAssessmentCalendarEntry[];
}

/** A module row in the repeated Section 6.9 week grid. */
export interface DescriptorAssessmentCalendarModule {
  title: string;
  /** Display text for weeks 1-15, indexed from zero for template access. */
  weeks: string[];
}

/** A stage/teaching-period assessment calendar rendered as one QQI grid. */
export interface DescriptorAssessmentCalendarTable {
  versionLabel: string;
  stageName: string;
  teachingPeriodValue: string;
  modules: DescriptorAssessmentCalendarModule[];
}

/**
 * A single entry in the descriptor's assessment strategy. One entry per
 * assessment across every programme module, carrying the owning module's code
 * and the assessment's identifying detail so the strategy reflects every
 * component rather than an over-aggregated summary (PD-083).
 */
export interface DescriptorAssessmentStrategyEntry {
  moduleCode: string;
  title: string;
  type: string;
  weighting: number;
}

/** PLO assessment mapping for the {FOR plo IN ploAssessmentMap} loop. */
export interface DescriptorPloAssessment {
  index: number;
  moduleMimloText: string;
  assessmentTechniques: string;
  weightings: string;
  /**
   * CAS component awards (code and title) whose modules address this outcome;
   * empty for programmes without CAS-aligned modules.
   */
  casComponents: string;
}

export interface DescriptorAppendixEvidence {
  moduleCode: string;
  mimloCode: string;
  mimloText: string;
  assessments: string[];
  assessmentSummary: string;
}

export type DescriptorAppendixStrandId = AppendixStrandId;

export interface DescriptorAppendixRelationship {
  standardId: string;
  indicatorId: string;
  criteria: string;
  thread: string;
  strandId: DescriptorAppendixStrandId | null;
  miploCode: string;
  miploText: string;
  evidence: DescriptorAppendixEvidence[];
}

export interface DescriptorAppendixStandardDescriptor {
  standardId: string;
  standardName: string;
  indicatorId: string;
  criteria: string;
  thread: string;
  label: string;
  text: string;
}

export interface DescriptorAppendixStrand {
  id: DescriptorAppendixStrandId;
  label: string;
  levelNMinusOneDescriptors: DescriptorAppendixStandardDescriptor[];
  levelNDescriptors: DescriptorAppendixStandardDescriptor[];
  relationships: DescriptorAppendixRelationship[];
}

export interface DescriptorAppendixStrands {
  knowledgeBreadth: DescriptorAppendixStrand;
  knowhowSkill: DescriptorAppendixStrand;
  competenceContext: DescriptorAppendixStrand;
  competenceRole: DescriptorAppendixStrand;
  competenceLearning: DescriptorAppendixStrand;
  competenceInsight: DescriptorAppendixStrand;
}

export interface DescriptorBuildContext {
  awardStandards?: AwardStandard[];
  /**
   * The QQI CAS awards dataset (cas_awards_lean.json), so CAS mappers can
   * resolve authoritative major-award and component titles.
   */
  casAwards?: CasAwardsData;
  /** Programme Lead contact for the Programme Manager fields. */
  programmeLeadContact?: ProviderContact;
  /** Limits the descriptor to one programme version and its effective assessments. */
  versionId?: string;
}

/** Full data object passed to docx-templates createReport(). */
/** Descriptor §1B.1 attendance-mode tick boxes derived from version data. */
export interface ProgrammeVersionTicks {
  fullTime: boolean;
  partTime: boolean;
  other: boolean;
}

/**
 * Descriptor §1B delivery-location tick boxes derived from each version's
 * delivery pattern. The QQI template splits blended delivery into
 * "Onsite + Synchronous / Asynchronous", so each delivery-pattern percentage
 * maps to a distinct location tick (PD-079).
 */
export interface DescriptorDeliveryTicks {
  /** On-campus delivery at the provider's premises (`onCampusPct > 0`). */
  onSite: boolean;
  /** Asynchronous directed learning undertaken off-premises (`asyncDirectedPct > 0`). */
  offSite: boolean;
  /** Synchronous online delivery (`syncOnlinePct > 0`). */
  online: boolean;
}

/**
 * A single row of the February 2026 §1B.1 delivery-mode matrix. One row per
 * programme version, carrying the version's label, its full-time/part-time
 * classification, and a boolean flag for each of the six QQI delivery modes.
 * Replaces the obsolete single FT/PT/Other row; derived from the same
 * version/delivery data resolved for the attendance and modality ticks
 * (PD-140).
 */
export interface DescriptorDeliveryMatrixRow {
  /** The owning version's label (e.g. "Full-time"). */
  versionLabel: string;
  /** Version classified as full-time attendance. */
  fullTime: boolean;
  /** Version classified as part-time attendance. */
  partTime: boolean;
  /** On-site (face-to-face, on-campus) delivery. */
  onSite: boolean;
  /** Synchronous hybrid (blended) delivery. */
  synchronousHybrid: boolean;
  /** Synchronous online delivery. */
  synchronousOnline: boolean;
  /** Asynchronous directed learning. */
  asynchronous: boolean;
  /** Independent directed learning. */
  independent: boolean;
  /** Work-based learning. */
  workBased: boolean;
}

export interface DescriptorData {
  // Simple fields
  programme_title: string;
  provider_name: string;
  provider_address: string;
  provider_website: string;
  provider_qa_procedures_url: string;
  validation_contact_name: string;
  validation_contact_title: string;
  validation_contact_address: string;
  validation_contact_email: string;
  validation_contact_phone: string;
  validation_contact_mobile: string;
  programme_manager_name: string;
  programme_manager_title: string;
  programme_manager_address: string;
  programme_manager_email: string;
  programme_manager_phone: string;
  programme_manager_mobile: string;
  /** Date of the latest saved programme revision, formatted for the descriptor cover. */
  document_date: string;
  award_class: string;
  award_type: string;
  isced_code: string;
  approved_hours_per_credit: string;
  /**
   * §1C.1.3 NFQ level of the principal programme. Carries the level string when
   * the programme's `nfqLevel` is set and the {@link MANUAL_COMPLETION_REQUIRED}
   * marker when it is null/undefined (PD-108).
   */
  nfq_level: string;
  /** §1C.1.3 NFQ level of the principal programme. Mirrors {@link nfq_level}. */
  principal_programme_nfq_level: string;
  ects: string;
  maximum_annual_ects: string;
  award_standard_name: string;
  miplos: string;
  miploEntries: DescriptorMiploEntry[];

  /** Existing version data rendered in the principal-programme application section. */
  principal_programme_duration: string;
  principal_programme_duration_years: string;
  principal_programme_intakes: string;
  principal_programme_cohort: string;
  assessment_integrity_summary: string;
  learning_environment_summary: string;
  programme_synopsis: string;
  graduate_attributes: string;
  target_group: string;
  tla_strategy: string;
  access_requirements: string;
  rpl_arrangements: string;
  transfer_progression: string;
  inwardTransferArrangements: DescriptorTransferProgressionArrangement[];
  inwardProgressionArrangements: DescriptorTransferProgressionArrangement[];
  outwardTransferArrangements: DescriptorTransferProgressionArrangement[];
  outwardProgressionArrangements: DescriptorTransferProgressionArrangement[];
  staffProfiles: DescriptorStaffProfile[];
  resourceEntries: DescriptorResourceRequirement[];
  psrb_status: string;
  psrb_details: string;
  psrb_submission_required: string;
  psrb_name: string;
  psrb_website: string;
  psrb_approval_timing: string;
  staffing_plan: string;
  resource_requirements: string;

  /**
   * Programme rationale narrative (§1B). A free-text section authored manually
   * rather than derived from the structured programme data; when no rationale
   * has been entered it carries the {@link MANUAL_COMPLETION_REQUIRED} marker so
   * the export flags it for manual completion instead of rendering blank
   * (PD-091).
   */
  programme_rationale: string;

  /**
   * Descriptor §1B.1 attendance-mode ticks, derived from the programme's
   * version data. A version whose label/code reads full-time or part-time ticks
   * the matching box; anything else (e.g. blended/online-only delivery labels)
   * ticks "Other" (PD-078).
   */
  programmeVersionTicks: ProgrammeVersionTicks;

  /**
   * Descriptor §1B delivery-location ticks, derived from each version's
   * delivery pattern. On-campus delivery ticks on-site, synchronous online
   * delivery ticks online, and asynchronous directed learning ticks off-site;
   * a 100% on-campus (face-to-face) version therefore ticks on-site only
   * (PD-079).
   */
  descriptorDeliveryTicks: DescriptorDeliveryTicks;

  /**
   * February 2026 §1B.1 delivery-mode matrix. One row per programme version,
   * each carrying the version's label, its full-time/part-time classification,
   * and boolean flags for the six delivery modes (on-site, synchronous-hybrid,
   * synchronous-online, asynchronous, independent, work-based). Replaces the
   * obsolete single FT/PT/Other row; derived from the same version/delivery
   * data as {@link programmeVersionTicks} and {@link descriptorDeliveryTicks}
   * (PD-140).
   */
  deliveryModeMatrix: DescriptorDeliveryMatrixRow[];

  /**
   * Comma-separated list of intake months (e.g. "September, January, May").
   * Sourced from the versions' `intakes`; empty string when none set.
   */
  intake_months: string;

  // Repeating sections
  modules: DescriptorModule[];
  ploAssessmentMap: DescriptorPloAssessment[];

  /**
   * Descriptor §6.1 Stage Level Outline. One entry per stage across every
   * programme version, listing the stage's name, sequence, credit target,
   * module count, and embedded exit award title (empty when none). Sourced from
   * each version's `stages` (PD-080).
   */
  stageLevelOutline: DescriptorStageOutline[];

  /**
   * Descriptor §6.2 Module Level Outline. One entry per programme module,
   * listing the module's code, title, credits, mandatory/elective status, stage
   * placement, and learning-outcome / assessment counts. Sourced from the
   * programme's `modules` (PD-081).
   */
  moduleLevelOutline: DescriptorModuleOutline[];

  /**
   * Descriptor §6.2 module-level outline grouped by version and stage.
   * Used by the template to emit a distinct heading and module table block for
   * each stage instead of placing every module beneath the first static stage.
   */
  moduleLevelOutlineStages: DescriptorModuleOutlineStage[];

  /**
   * Descriptor §6.9 Indicative Assessment Calendar. One row per indicative week
   * that carries at least one assessment, each row listing the assessments whose
   * `indicativeWeek` matches that week. Rows are ordered by ascending week and
   * are sourced from every module's assessments; assessments without an
   * `indicativeWeek` are omitted (PD-082).
   */
  assessmentCalendar: DescriptorAssessmentCalendarRow[];
  assessmentCalendarTables: DescriptorAssessmentCalendarTable[];
  appendixRelationships: DescriptorAppendixRelationship[];
  appendixStrands: DescriptorAppendixStrands;

  /**
   * Descriptor assessment strategy. One entry per assessment across every
   * programme module, in module order then assessment order, each carrying the
   * owning module's code and the assessment's title, type, and weighting. The
   * strategy reflects every module assessment rather than a subset or an
   * over-aggregated summary (PD-083).
   */
  assessmentStrategy: DescriptorAssessmentStrategyEntry[];

  /**
   * Proposed programme schedule, embedded within the canonical descriptor
   * export as a section rather than as a separate standalone artifact. Sourced
   * from the shared schedule builder ({@link buildScheduleData}); one entry per
   * (version, stage) pairing (PD-074/PD-075).
   */
  scheduleSections: ScheduleStageData[];

  /**
   * The complete schedule data model produced by the shared schedule builder
   * ({@link buildScheduleData}). The descriptor's schedule section is generated
   * from this better-populated standalone logic; `scheduleSections` is its
   * `stages` projection (PD-076/PD-092).
   */
  scheduleData: ScheduleData;

  /**
   * Flat list of proposed programme schedule rows for descriptor §1B.5,
   * sourced from the shared schedule builder ({@link buildScheduleData}) by
   * flattening every stage's module rows. Empty when the programme has no
   * scheduled modules (PD-076).
   */
  proposedProgrammeScheduleRows: ScheduleModuleRow[];

  /** First-class HE embedded and exit awards, excluding CAS component awards. */
  embeddedAwardPathways: DescriptorAwardPathway[];

  /** Human-readable embedded-award summary for the HE descriptor template. */
  embedded_award_summary: string;

  /** Structured HE embedded-award records for repeated descriptor sections. */
  embeddedAwardRecords: DescriptorEmbeddedSection[];

  /** CAS component/minor awards, kept separate from HE embedded and exit awards. */
  casComponentAwards: DescriptorAwardPathway[];

  /** Elective choice slots and their options, including the representative option. */
  electiveChoiceSlots: DescriptorElectiveChoiceSlot[];

  /** Human-readable elective-rule summary for the HE descriptor template. */
  elective_choice_summary: string;

  /** One deterministic learner route through mandatory and elective modules. */
  representativeProgrammePathway: DescriptorRepresentativePathway;

  /**
   * Embedded-programme sections. Present only when the programme defines a
   * first-class HE award or legacy embedded award; omitted entirely otherwise
   * so the template suppresses the embedded-programme blocks (PD-087).
   */
  embeddedProgrammeSections?: DescriptorEmbeddedSection[];

  /**
   * Remaining required-data blockers for a complete descriptor export. The
   * export is always produced as a partial preview; this list names the
   * still-missing required items so the user can complete them. Empty when the
   * programme has all required data (PD-019).
   */
  exportBlockers: string[];

  /** Conditional/dynamic sections (e.g. {@link embeddedProgrammeSections}). */
  [key: string]: unknown;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Marker rendered into a narrative descriptor section when no content has been
 * authored for it. Narrative sections (e.g. the programme rationale) are free
 * text completed manually rather than derived from the structured programme
 * data, so an unpopulated section is explicitly flagged for manual completion
 * rather than exported as a silent blank (PD-091).
 */
const MANUAL_COMPLETION_REQUIRED = "manual completion required";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

/** Format a saved programme revision date for the descriptor cover. */
function formatDocumentDate(value: string | null | undefined): string {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return `${date.getUTCDate()} ${MONTH_NAMES[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
}

/**
 * Resolve a free-text narrative section value, returning the authored content
 * when present and the {@link MANUAL_COMPLETION_REQUIRED} marker when the
 * section is unpopulated (missing, non-string, or whitespace only) (PD-091).
 */
function narrativeSection(value: unknown): string {
  const text = typeof value === "string" ? value.trim() : "";
  return text || MANUAL_COMPLETION_REQUIRED;
}

/** Format a number as a string, returning empty string for 0/undefined. */
function fmtNum(n: number | undefined): string {
  return n ? String(n) : "";
}

/** Whether a PSRB submission is required, based on the recorded PSRB status. */
function psrbSubmissionRequired(
  psrbStatus: "not-applicable" | "required" | "pending" | "approved" | undefined,
): string {
  if (psrbStatus == null) {
    return "";
  }
  if (psrbStatus === "not-applicable") {
    return "No";
  }
  return "Yes";
}

/** Format a percentage, returning empty string for 0. */
function fmtPct(n: number): string {
  return n > 0 ? `${n}%` : "";
}

/**
 * Build the related MIPLO text for a MIMLO.
 * Looks up which programme outcomes reference this MIMLO ID.
 */
function getRelatedPlos(
  mimloId: string,
  plos: PLO[],
  ploToMimlos: Record<string, string[]>,
): string {
  const related: string[] = [];
  plos.forEach((plo, i) => {
    const mimloIds = ploToMimlos[plo.id] ?? [];
    if (mimloIds.includes(mimloId)) {
      related.push(plo.code?.trim() || `MIPLO ${i + 1}`);
    }
  });
  return related.join(", ");
}

/**
 * Build the MIMLO text for an assessment (which MIMLOs it assesses).
 */
function getAssessmentMimloText(assessment: ModuleAssessment, mod: Module): string {
  const mimloIds = assessment.mimloIds ?? [];
  if (mimloIds.length === 0) {
    return "";
  }
  const mimlos = mod.mimlos ?? [];
  return mimloIds
    .map((id) => {
      const idx = mimlos.findIndex((m) => m.id === id);
      return idx >= 0 ? `MIMLO ${idx + 1}` : "";
    })
    .filter(Boolean)
    .join(", ");
}

/**
 * Find which stage a module belongs to.
 */
function normalizeStageValue(value: string | number | undefined): string {
  if (value == null) {
    return "";
  }
  const text = String(value).trim();
  if (!text) {
    return "";
  }
  const withoutPrefix = text.replace(/^stage\s+/i, "").replace(/^semester\s+/i, "");
  return withoutPrefix.replace(/^teaching period\s+/i, "").trim();
}

function findModuleStage(
  moduleId: string,
  versions: ProgrammeVersion[],
): { stageName: string; semester: string; durationWeeks: number; deliveryKey: string } {
  const allStages = versions.flatMap((v) => (v.stages ?? []).map((stage) => ({ v, stage })));
  const found = allStages.find(({ stage }) =>
    (stage.modules ?? []).some((sm) => sm.moduleId === moduleId),
  );
  if (found) {
    const { v, stage } = found;
    const stageModule = (stage.modules ?? []).find((sm) => sm.moduleId === moduleId);
    if (stageModule) {
      const stageNameValue =
        stage.academicStage != null ? stage.academicStage : (stage.sequence ?? stage.name ?? "");
      const semesterValue = stageModule.semester ?? "";
      return {
        stageName: normalizeStageValue(stageNameValue),
        semester: normalizeStageValue(semesterValue),
        durationWeeks: moduleTeachingWeeks(stage, semesterValue) ?? 0,
        deliveryKey: `${v.id}_${v.deliveryModality}`,
      };
    }
  }
  return { stageName: "", semester: "", durationWeeks: 0, deliveryKey: "" };
}

/** Intake months across every version. */
export function programmeIntakeMonths(p: Programme): string[] {
  return [...new Set((p.versions ?? []).flatMap((version) => version.intakes ?? []))];
}

function durationYears(versions: ProgrammeVersion[]): string {
  const years = versions.map(versionDurationYears).find((count) => count !== undefined);
  return years === undefined ? "" : String(years);
}

/**
 * Classify a programme version's attendance mode for descriptor §1B.1.
 * Full-time / part-time are detected from the version label or code; anything
 * else (e.g. blended/online-only delivery labels) counts as "Other" (PD-078).
 */
function classifyVersionMode(v: ProgrammeVersion): keyof ProgrammeVersionTicks {
  const text = `${v.label ?? ""} ${v.code ?? ""}`.toLowerCase();
  const code = (v.code ?? "").trim().toLowerCase();
  if (/part[\s_-]*time/.test(text) || code.startsWith("pt") || code === "opt") {
    return "partTime";
  }
  if (/full[\s_-]*time/.test(text) || code === "ft") {
    return "fullTime";
  }
  return "other";
}

/** Aggregate descriptor §1B.1 FT/PT/Other ticks across all programme versions. */
function buildProgrammeVersionTicks(versions: ProgrammeVersion[]): ProgrammeVersionTicks {
  const modes = versions.map(classifyVersionMode);
  return {
    fullTime: modes.includes("fullTime"),
    partTime: modes.includes("partTime"),
    other: modes.includes("other"),
  };
}

/** Delivery modalities that carry a valid default pattern split. */
const KNOWN_DELIVERY_MODALITIES = new Set(["F2F", "BLENDED", "ONLINE"]);

/**
 * Resolve a version's delivery pattern for its selected modality, falling back
 * to the modality default when no explicit split is stored.
 */
function resolveDeliveryPattern(
  v: ProgrammeVersion,
): { syncOnlinePct?: number; asyncDirectedPct?: number; onCampusPct?: number } | undefined {
  const rawModality = v.deliveryModality;
  if (!rawModality) {
    return undefined;
  }
  const mod = normalizeDeliveryModality(rawModality);
  const patterns = v.deliveryPatterns ?? {};
  const stored =
    patterns[rawModality] ??
    patterns[mod] ??
    Object.entries(patterns).find(([key]) => normalizeDeliveryModality(key) === mod)?.[1];
  if (stored) {
    return stored;
  }
  return KNOWN_DELIVERY_MODALITIES.has(mod) ? defaultPatternFor(mod) : undefined;
}

/**
 * Build the February 2026 §1B.1 delivery-mode matrix: one row per programme
 * version carrying its label, full-time/part-time classification, and the six
 * delivery-mode flags. The mode flags mirror the per-stage modality ticks — the
 * delivery modality drives on-site/synchronous-hybrid/synchronous-online, and a
 * resolved asynchronous-directed split drives asynchronous/independent
 * (PD-140).
 */
function buildDeliveryModeMatrix(versions: ProgrammeVersion[]): DescriptorDeliveryMatrixRow[] {
  return versions.map((v) => {
    const mode = classifyVersionMode(v);
    const dm = normalizeDeliveryModality(v.deliveryModality);
    const pattern = resolveDeliveryPattern(v);
    const hasAsyncDirected = Number(pattern?.asyncDirectedPct ?? 0) > 0;
    return {
      versionLabel: v.label ?? "",
      fullTime: mode === "fullTime",
      partTime: mode === "partTime",
      onSite: dm === "F2F",
      synchronousHybrid: dm === "BLENDED",
      synchronousOnline: dm === "ONLINE",
      asynchronous: hasAsyncDirected,
      independent: hasAsyncDirected,
      workBased: dm === "APPRENTICESHIP",
    };
  });
}

/**
 * Aggregate descriptor §1B on-site/off-site/online delivery ticks across all
 * programme versions. On-campus delivery ticks on-site, synchronous online
 * delivery ticks online, and asynchronous directed learning ticks off-site;
 * a 100% on-campus face-to-face version ticks on-site only (PD-079).
 */
function buildDescriptorDeliveryTicks(versions: ProgrammeVersion[]): DescriptorDeliveryTicks {
  const ticks: DescriptorDeliveryTicks = { onSite: false, offSite: false, online: false };
  versions.forEach((v) => {
    const pat = resolveDeliveryPattern(v);
    if (pat) {
      if (Number(pat.onCampusPct ?? 0) > 0) {
        ticks.onSite = true;
      }
      if (Number(pat.asyncDirectedPct ?? 0) > 0) {
        ticks.offSite = true;
      }
      if (Number(pat.syncOnlinePct ?? 0) > 0) {
        ticks.online = true;
      }
    }
  });
  return ticks;
}

// ============================================================================
// Strand Mapping
// ============================================================================

function normaliseStandardLocation(value: string): string {
  return value.toLowerCase().replaceAll("-", " ").replaceAll("&", "and").replace(/\s+/g, " ");
}

function assessmentTechnique(type: string | undefined, title: string | undefined): string {
  const cleanType = (type ?? "").trim();
  const cleanTitle = (title ?? "").trim();
  if (!cleanType && !cleanTitle) {
    return "";
  }
  if (cleanType && cleanTitle && cleanType !== cleanTitle) {
    return `${cleanType} (${cleanTitle})`;
  }
  return cleanTitle || cleanType;
}

/**
 * Build the Appendix 1 strand mapping data.
 * For each strand, find PLOs whose standardMappings reference it.
 */
function buildAppendixRelationships(
  plos: PLO[],
  ploToMimlos: Record<string, string[]>,
  modules: Module[],
  defaultStandardId: string,
): DescriptorAppendixRelationship[] {
  return plos.flatMap((plo, ploIndex) => {
    const miploCode = plo.code?.trim() || `MIPLO ${ploIndex + 1}`;
    const linkedMimloIds = new Set(ploToMimlos[plo.id] ?? []);
    const evidence: DescriptorAppendixEvidence[] = [];

    modules.forEach((mod) => {
      (mod.mimlos ?? []).forEach((mimlo, mimloIndex) => {
        if (!linkedMimloIds.has(mimlo.id)) {
          return;
        }
        const assessments = (mod.assessments ?? [])
          .filter((assessment) => (assessment.mimloIds ?? []).includes(mimlo.id))
          .map((assessment) => {
            const technique = assessmentTechnique(assessment.type, assessment.title);
            return assessment.weighting == null
              ? technique
              : `${technique} (${assessment.weighting}%)`;
          });
        evidence.push({
          moduleCode: mod.code || mod.title,
          mimloCode: `MIMLO ${mimloIndex + 1}`,
          mimloText: mimlo.text,
          assessments,
          assessmentSummary:
            assessments.length > 0 ? assessments.join("; ") : "No mapped assessment",
        });
      });
    });

    return (plo.standardMappings ?? []).map((mapping) => ({
      standardId: mapping.standardId ?? defaultStandardId,
      indicatorId: mapping.indicatorId ?? "",
      criteria: mapping.criteria ?? "",
      thread: mapping.thread ?? "",
      strandId: resolveAppendixStrand(mapping.criteria ?? "", mapping.thread ?? ""),
      miploCode,
      miploText: plo.text,
      evidence,
    }));
  });
}

function resolveAppendixIndicator(
  standard: AwardStandard,
  nfqLevel: number,
  relationship: DescriptorAppendixRelationship,
): ReturnType<typeof getStandardIndicators>[number] | undefined {
  const indicators = getStandardIndicators(standard, nfqLevel);
  const canonicalMatch = indicators.find((indicator) => indicator.id === relationship.indicatorId);
  if (canonicalMatch) {
    return canonicalMatch;
  }

  const criteria = normaliseStandardLocation(relationship.criteria);
  const thread = normaliseStandardLocation(relationship.thread);
  const exactMatch = indicators.find(
    (indicator) =>
      normaliseStandardLocation(indicator.criteria) === criteria &&
      normaliseStandardLocation(indicator.thread) === thread,
  );
  if (exactMatch) {
    return exactMatch;
  }

  const strand = resolveAppendixStrand(relationship.criteria, relationship.thread);
  return indicators.find(
    (indicator) =>
      strand !== null &&
      resolveAppendixStrand(indicator.criteria, indicator.thread) === strand &&
      normaliseStandardLocation(indicator.thread) === thread,
  );
}

function buildAppendixStandardDescriptors(
  relationships: DescriptorAppendixRelationship[],
  standards: AwardStandard[],
  programmeLevel: number,
  descriptorLevel: number,
): DescriptorAppendixStandardDescriptor[] {
  const descriptors = new Map<string, DescriptorAppendixStandardDescriptor>();
  const standardsById = new Map(standards.map((standard) => [standard.id, standard]));

  relationships.forEach((relationship) => {
    const standard = standardsById.get(relationship.standardId);
    if (!standard) {
      return;
    }

    const mappedIndicator = resolveAppendixIndicator(standard, programmeLevel, relationship);
    if (!mappedIndicator) {
      return;
    }
    const comparisonRelationship = {
      ...relationship,
      indicatorId: "",
      criteria: mappedIndicator.criteria,
      thread: mappedIndicator.thread,
    };
    let descriptorStandard = standard;
    let descriptorIndicator =
      descriptorLevel === programmeLevel
        ? mappedIndicator
        : resolveAppendixIndicator(standard, descriptorLevel, comparisonRelationship);
    if (!descriptorIndicator && descriptorLevel !== programmeLevel) {
      const nfqGrid = standardsById.get(NFQ_GRID_STANDARD_ID);
      const fallbackIndicator = nfqGrid
        ? resolveAppendixIndicator(nfqGrid, descriptorLevel, comparisonRelationship)
        : undefined;
      if (nfqGrid && fallbackIndicator) {
        descriptorStandard = nfqGrid;
        descriptorIndicator = fallbackIndicator;
      }
    }
    if (!descriptorIndicator) {
      return;
    }

    const key = `${descriptorStandard.id}:${descriptorIndicator.id}`;
    if (!descriptors.has(key)) {
      descriptors.set(key, {
        standardId: descriptorStandard.id,
        standardName: descriptorStandard.name,
        indicatorId: descriptorIndicator.id,
        criteria: descriptorIndicator.criteria,
        thread: descriptorIndicator.thread,
        label: `${descriptorStandard.name} - ${descriptorIndicator.criteria} / ${descriptorIndicator.thread}`,
        text: descriptorIndicator.descriptorText?.trim() || descriptorIndicator.descriptor.trim(),
      });
    }
  });

  return [...descriptors.values()];
}

function buildAppendixStrands(
  relationships: DescriptorAppendixRelationship[],
  standards: AwardStandard[],
  programmeLevel: number,
): DescriptorAppendixStrands {
  const strand = (id: DescriptorAppendixStrandId, label: string): DescriptorAppendixStrand => {
    const strandRelationships = relationships.filter(
      (relationship) => relationship.strandId === id,
    );
    return {
      id,
      label,
      levelNMinusOneDescriptors: buildAppendixStandardDescriptors(
        strandRelationships,
        standards,
        programmeLevel,
        programmeLevel - 1,
      ),
      levelNDescriptors: buildAppendixStandardDescriptors(
        strandRelationships,
        standards,
        programmeLevel,
        programmeLevel,
      ),
      relationships: strandRelationships,
    };
  };

  return {
    knowledgeBreadth: strand("knowledge_breadth", "Knowledge: breadth and kind"),
    knowhowSkill: strand("knowhow_skill", "Know-how and skill: range and selectivity"),
    competenceContext: strand("competence_context", "Competence: context"),
    competenceRole: strand("competence_role", "Competence: role"),
    competenceLearning: strand("competence_learning", "Competence: learning-to-learn"),
    competenceInsight: strand("competence_insight", "Competence: insight"),
  };
}

function computeAppendixDescriptorBlockers(
  p: Programme,
  strands: DescriptorAppendixStrands,
  context: DescriptorBuildContext,
): string[] {
  if (isFeAwardType(p.awardType ?? "") || context.awardStandards === undefined) {
    return [];
  }

  const blockers: string[] = [];
  Object.values(strands).forEach((strand) => {
    if (strand.relationships.length === 0) {
      blockers.push(`Appendix 1: no MIPLO is mapped to ${strand.label}`);
      return;
    }
    if (strand.levelNDescriptors.length === 0) {
      blockers.push(`Appendix 1: Level N standard text could not be resolved for ${strand.label}`);
    }
    if (strand.levelNMinusOneDescriptors.length === 0) {
      blockers.push(
        `Appendix 1: Level N-1 standard text could not be resolved for ${strand.label}`,
      );
    }
  });

  return blockers;
}

// ============================================================================
// Embedded Programme
// ============================================================================

/**
 * Build the embedded-programme descriptor sections.
 *
 * First-class HE awards take precedence over the deprecated single embedded
 * programme field. Returns `undefined` when neither exists, so the template
 * suppresses the embedded-programme blocks (PD-087).
 */
function buildEmbeddedSections(p: Programme): DescriptorEmbeddedSection[] | undefined {
  const firstClassAwards = (p.embeddedAwards ?? [])
    .filter((award) => award.kind !== "cas-component")
    .map((award) => ({
      title: award.title ?? "",
      awardType: award.awardType ?? "",
      nfqLevel: award.nfqLevel != null ? String(award.nfqLevel) : "",
      ects: award.credits != null ? String(award.credits) : "",
      equivalentEctsValue: Number(award.credits ?? 0) === Number(p.totalCredits ?? 0),
      // QQI Exit-only: offered only on exit, never taken on its own.
      exitOnly: award.kind === "exit",
    }));
  if (firstClassAwards.length > 0) {
    return firstClassAwards;
  }

  const embedded = p.embeddedProgramme;
  if (!embedded) {
    return undefined;
  }
  return [
    {
      title: embedded.title ?? "",
      awardType: embedded.awardType ?? "",
      nfqLevel: embedded.nfqLevel != null ? String(embedded.nfqLevel) : "",
      ects: embedded.totalCredits != null ? String(embedded.totalCredits) : "",
      equivalentEctsValue: Number(embedded.totalCredits ?? 0) === Number(p.totalCredits ?? 0),
      exitOnly: false,
    },
  ];
}

function pathwayModule(module: Module): DescriptorPathwayModule {
  return {
    code: module.code ?? "",
    title: module.title ?? "",
    credits: Number(module.credits ?? 0),
  };
}

function awardRelationship(award: EmbeddedAward): string {
  const labels: Record<EmbeddedAward["kind"], string> = {
    embedded: "Embedded award",
    exit: "Exit award",
    "embedded-and-exit": "Embedded and exit award",
    "cas-component": "CAS component award",
  };
  return labels[award.kind];
}

function buildAwardPathways(p: Programme): DescriptorAwardPathway[] {
  const versions = p.versions ?? [];
  return (p.embeddedAwards ?? []).map((award) => {
    const projection = getAwardCreditProjection(p, award);
    const linkedStageIds = new Set(award.stageIds ?? []);
    const stageLabels = versions.flatMap((version) =>
      (version.stages ?? [])
        .filter((stage) => linkedStageIds.has(stage.id))
        .map((stage) => {
          const versionLabel =
            [version.label, version.code].find(
              (value) => typeof value === "string" && value.trim() !== "",
            ) ?? "Programme version";
          return `${versionLabel}: ${stage.name ?? "Untitled stage"}`;
        }),
    );

    return {
      id: award.id,
      title: award.title ?? "",
      awardType: award.awardType ?? "",
      relationship: awardRelationship(award),
      nfqLevel: award.nfqLevel != null ? String(award.nfqLevel) : "",
      credits: Number(award.credits ?? 0),
      casComponentCode: award.casComponentCode ?? "",
      versionLabels: projection.applicableVersionLabels,
      stageLabels,
      modules: projection.linkedModules.map(pathwayModule),
      minimumCredits: projection.minimumCredits,
      maximumCredits: projection.maximumCredits,
      creditStatus: projection.status,
      notes: award.notes ?? "",
    };
  });
}

function buildElectiveChoiceSlots(p: Programme): DescriptorElectiveChoiceSlot[] {
  const modules = p.modules ?? [];
  const pathway = getProgrammePathwayProjection(p);

  return (p.electiveDefinitions ?? []).map((choiceSlot) => {
    const pathwaySlot = pathway.choiceSlots.find(
      (candidate) => candidate.choiceSlot.id === choiceSlot.id,
    );
    const representativeId = pathwaySlot?.representativeOption?.option.id;
    const options = (choiceSlot.groups ?? []).map((option) => {
      const selection = getElectiveOptionSelection(option, modules);
      const isRepresentative = option.id === representativeId;
      return {
        id: option.id,
        name: option.name ?? "",
        code: option.code ?? "",
        selectionRule: selection.requiresModuleChoice
          ? `Choose ${selection.requiredModuleCount} of ${selection.optionModules.length} modules`
          : "Complete all modules in this option",
        requiredModuleCount: selection.requiredModuleCount,
        availableModuleCount: selection.optionModules.length,
        availableModules: selection.optionModules.map(pathwayModule),
        representativeModules: isRepresentative ? selection.selectedModules.map(pathwayModule) : [],
        representativeCredits: isRepresentative ? selection.credits : 0,
        isRepresentative,
        complete:
          selection.selectionCountValid &&
          selection.selectionComplete &&
          Number(choiceSlot.credits ?? 0) > 0 &&
          selection.credits === Number(choiceSlot.credits ?? 0),
      };
    });

    return {
      id: choiceSlot.id,
      name: choiceSlot.name ?? "",
      code: choiceSlot.code ?? "",
      requiredCredits: Number(choiceSlot.credits ?? 0),
      representativeOptionName: pathwaySlot?.representativeOption?.option.name ?? "",
      representativeCredits: pathwaySlot?.representativeOption?.credits ?? 0,
      complete: pathwaySlot?.complete ?? false,
      options,
    };
  });
}

function buildRepresentativeProgrammePathway(p: Programme): DescriptorRepresentativePathway {
  const pathway = getProgrammePathwayProjection(p);
  return {
    modules: pathway.selectedModules.map(pathwayModule),
    declaredCredits: pathway.declaredCredits,
    projectedCredits: pathway.projectedCredits,
    creditStatus: pathway.status,
    incompleteChoiceSlots: pathway.incompleteChoiceSlots,
  };
}

function buildEmbeddedAwardSummary(awardPathways: DescriptorAwardPathway[]): string {
  return awardPathways
    .map((award) => {
      const creditText = award.credits > 0 ? ` (${award.credits} ECTS)` : "";
      return `${award.title}${creditText}`;
    })
    .join("; ");
}

function buildElectiveChoiceSummary(electiveChoiceSlots: DescriptorElectiveChoiceSlot[]): string {
  return electiveChoiceSlots
    .map((slot) => {
      const optionsText = slot.options
        .map((option) => `${option.name} (${option.selectionRule})`)
        .join("; ");
      return `${slot.name}${slot.requiredCredits > 0 ? ` (${slot.requiredCredits} ECTS)` : ""}: ${optionsText}`;
    })
    .join("\n");
}

/**
 * Build the descriptor §6.1 Stage Level Outline.
 *
 * Produces one entry per stage across every programme version, in version then
 * stage order, capturing each stage's name, sequence, credit target, module
 * count, and the exit awards whose pathway ends there (PD-080).
 */
function buildStageLevelOutline(
  programme: Programme,
  versions: ProgrammeVersion[],
): DescriptorStageOutline[] {
  const outline: DescriptorStageOutline[] = [];
  versions.forEach((version) => {
    const exitAwards = exitAwardsByStage(programme, version);
    (version.stages ?? []).forEach((stage) => {
      const awards = exitAwards.get(stage.id);
      outline.push({
        versionLabel: version.label ?? "",
        stageName: stage.name ?? "",
        sequence: stage.sequence ?? 0,
        creditsTarget: stage.creditsTarget ?? 0,
        moduleCount: (stage.modules ?? stage.moduleIds ?? []).length,
        hasExitAward: awards !== undefined,
        exitAwardTitle: exitAwardTitle(awards),
      });
    });
  });
  return outline;
}

/**
 * Read a module's short narrative for §6.2 from the source fields that may carry
 * one (`synopsis`, then `description`). The programme schema has no dedicated
 * synopsis field, so both are typed as `unknown` on `Module`; a non-empty string
 * on either is returned, otherwise an empty string — an absent narrative is a
 * genuine blank, never invented content (PD-081).
 */
function readModuleSynopsis(mod: Module): string {
  const synopsis = mod.synopsis;
  if (typeof synopsis === "string" && synopsis.trim() !== "") {
    return synopsis;
  }
  const description = mod.description;
  if (typeof description === "string" && description.trim() !== "") {
    return description;
  }
  return "";
}

function outlinePlacement(mandatoryElective: string, stage: string, semester: string): string {
  return [mandatoryElective, stage && `Stage ${stage}`, semester && `Semester ${semester}`]
    .filter(Boolean)
    .join(", ");
}

/**
 * Build the descriptor §6.2 Module Level Outline.
 *
 * Produces one entry per programme module, in module order, capturing each
 * module's code, title, credit value, mandatory/elective status, stage
 * placement (resolved from version stage assignments, falling back to the
 * module's own stage/semester), and the count of its learning outcomes and
 * assessments (PD-081).
 */
function buildModuleLevelOutline(
  modules: Module[],
  versions: ProgrammeVersion[],
): DescriptorModuleOutline[] {
  return modules.map((mod) => {
    const { stageName, semester: stageSemester } = findModuleStage(mod.id, versions);
    const mandatoryElective = mod.isElective ? "E" : "M";
    const stage = mod.stage != null ? String(mod.stage) : stageName;
    const semester = mod.semester != null ? String(mod.semester) : stageSemester;
    return {
      code: mod.code ?? "",
      title: mod.title ?? "",
      credits: mod.credits ?? 0,
      mandatoryElective,
      stage,
      semester,
      placement: outlinePlacement(mandatoryElective, stage, semester),
      mimloCount: (mod.mimlos ?? []).length,
      assessmentCount: (mod.assessments ?? []).length,
      synopsis: readModuleSynopsis(mod),
    };
  });
}

function buildModuleLevelOutlineStages(
  modules: Module[],
  versions: ProgrammeVersion[],
): DescriptorModuleOutlineStage[] {
  const modulesById = new Map(modules.map((module) => [module.id, module]));

  return versions.flatMap((version) =>
    (version.stages ?? []).map((stage) => {
      const stageModules: { moduleId: string; semester?: string }[] =
        stage.modules ?? (stage.moduleIds ?? []).map((moduleId) => ({ moduleId }));
      const stageName = stage.name ?? `Stage ${stage.sequence ?? ""}`.trim();
      const normalizedStage = normalizeStageValue(
        stage.academicStage ?? stage.sequence ?? stage.name,
      );

      return {
        versionLabel: version.label ?? "",
        stageName,
        modules: stageModules.flatMap((assignment) => {
          const module = modulesById.get(assignment.moduleId);
          if (!module) {
            return [];
          }
          const outline = buildModuleLevelOutline([module], versions)[0]!;
          const placedStage = normalizedStage || outline.stage;
          const semester = normalizeStageValue(assignment.semester ?? outline.semester);
          return [
            {
              ...outline,
              stage: placedStage,
              semester,
              placement: outlinePlacement(outline.mandatoryElective, placedStage, semester),
            },
          ];
        }),
      };
    }),
  );
}

/**
 * Build the descriptor §6.9 Indicative Assessment Calendar.
 *
 * Collects every assessment across all programme modules that carries an
 * `indicativeWeek` and groups them into one row per week, ordered by ascending
 * week. Each entry records the assessment's identifying detail and the owning
 * module so the calendar can be rendered with context. Assessments without an
 * `indicativeWeek` are omitted (PD-082).
 */
function buildAssessmentCalendar(modules: Module[]): DescriptorAssessmentCalendarRow[] {
  const byWeek = new Map<number, DescriptorAssessmentCalendarEntry[]>();
  modules.forEach((mod) => {
    (mod.assessments ?? []).forEach((asmt) => {
      const week = asmt.indicativeWeek;
      if (week == null) {
        return;
      }
      const entry: DescriptorAssessmentCalendarEntry = {
        moduleCode: mod.code ?? "",
        moduleTitle: mod.title ?? "",
        title: asmt.title ?? asmt.type ?? "",
        type: asmt.type ?? "",
        weighting: asmt.weighting ?? 0,
      };
      const existing = byWeek.get(week);
      if (existing) {
        existing.push(entry);
      } else {
        byWeek.set(week, [entry]);
      }
    });
  });
  return [...byWeek.keys()]
    .sort((a, b) => a - b)
    .map((week) => ({ week, assessments: byWeek.get(week) ?? [] }));
}

function calendarAssessmentText(
  assessment: ScheduleStageData["weeklyTables"][number]["modules"][number]["calendarCells"][number]["assessments"][number],
): string {
  const technique = assessmentTechnique(assessment.type, assessment.title);
  const weighting =
    assessment.weighting == null ? technique : `${technique} (${assessment.weighting}%)`;
  return assessment.outsideTeachingPeriod ? `${weighting} [outside teaching period]` : weighting;
}

function buildAssessmentCalendarTables(
  scheduleSections: ScheduleStageData[],
): DescriptorAssessmentCalendarTable[] {
  return scheduleSections.flatMap((section) =>
    section.weeklyTables.map((table) => ({
      versionLabel: section.versionLabel,
      stageName: table.stageName,
      teachingPeriodValue: table.teachingPeriodValue,
      modules: table.modules.flatMap((mod) => {
        const calendarRow: DescriptorAssessmentCalendarModule = {
          title: mod.title,
          weeks: Array.from({ length: 15 }, (_, index) => {
            const cell = mod.calendarCells.find((candidate) => candidate.week === index + 1);
            return cell?.assessments.map(calendarAssessmentText).join("; ") ?? "";
          }),
        };
        const outsideGridNotes = mod.calendarCells.flatMap((cell) =>
          cell.week > 15
            ? cell.assessments.map(
                (assessment) =>
                  `Week ${cell.week}${assessment.outsideTeachingPeriod ? " (outside teaching period)" : ""}: ${calendarAssessmentText(assessment)}`,
              )
            : [],
        );
        const unconfirmedNotes = mod.unscheduledAssessments.map(
          (assessment) =>
            `Outside teaching period, timing not confirmed: ${calendarAssessmentText(assessment)}`,
        );
        const timingNotes = [...outsideGridNotes, ...unconfirmedNotes];

        return timingNotes.length === 0
          ? [calendarRow]
          : [
              calendarRow,
              {
                title: `${mod.title}: ${timingNotes.join("; ")}`,
                weeks: Array.from({ length: 15 }, () => ""),
              },
            ];
      }),
    })),
  );
}

function versionSummary(
  versions: ProgrammeVersion[],
  valueFor: (version: ProgrammeVersion) => string,
): string {
  return versions
    .map((version) => {
      const value = valueFor(version).trim();
      return value ? `${version.label || version.code}: ${value}` : "";
    })
    .filter(Boolean)
    .join("\n");
}

function transferProgressionArrangements(
  arrangements: TransferProgressionArrangement[] | undefined,
  type: TransferProgressionArrangementType,
): DescriptorTransferProgressionArrangement[] {
  return (arrangements ?? [])
    .filter((arrangement) => arrangement.type === type)
    .map((arrangement) => ({
      programmeName: arrangement.programmeName,
      provider: arrangement.provider,
    }));
}

function staffProfiles(profiles: StaffProfile[] | undefined): DescriptorStaffProfile[] {
  return (profiles ?? []).map((profile) => ({
    role: profile.role,
    qualificationsExperience: profile.qualificationsExperience,
    numberRequired: profile.numberRequired != null ? String(profile.numberRequired) : "",
    inPost: profile.inPost != null ? String(profile.inPost) : "",
    toBeRecruited: profile.toBeRecruited != null ? String(profile.toBeRecruited) : "",
  }));
}

function resourceEntries(
  entries: ResourceRequirement[] | undefined,
): DescriptorResourceRequirement[] {
  return (entries ?? []).map((entry) => ({
    resource: entry.resource,
    quantity: entry.quantity,
    inPlace: entry.inPlace ? "✔" : "",
    toBeProcured: entry.toBeProcured ? "✔" : "",
  }));
}

function buildIntegritySummary(scheduleSections: ScheduleStageData[]): string {
  return scheduleSections
    .flatMap((section) =>
      section.integrityNotes.map(
        (note) =>
          `${section.versionLabel} / ${section.stageName} / ${note.moduleTitle} / ` +
          `${note.assessmentTitle}: ${note.flags.join(", ")}`,
      ),
    )
    .join("\n");
}

/**
 * Build the descriptor's assessment strategy.
 *
 * Flattens every assessment across all programme modules into a single ordered
 * list — module order then assessment order — so the strategy reflects every
 * module assessment rather than a subset or an over-aggregated CA/exam split.
 * Each entry records the owning module's code alongside the assessment's
 * identifying detail (PD-083).
 */
function buildAssessmentStrategy(modules: Module[]): DescriptorAssessmentStrategyEntry[] {
  return modules.flatMap((mod) =>
    (mod.assessments ?? []).map((asmt) => ({
      moduleCode: mod.code ?? "",
      title: asmt.title ?? asmt.type ?? "",
      type: asmt.type ?? "",
      weighting: asmt.weighting ?? 0,
    })),
  );
}

/**
 * Determine the remaining required-data blockers for a descriptor export.
 *
 * The descriptor export is always producible as a partial preview, even when
 * data is incomplete. This pure helper returns a human-readable list of the
 * required items that are still missing so the caller can surface them
 * alongside the preview. An empty array means the programme carries all the
 * required data for a complete descriptor (PD-019).
 *
 * @param p - The full programme state
 * @returns The list of remaining blockers (empty when complete)
 */
export function computeExportBlockers(p: Programme): string[] {
  const blockers: string[] = [];

  if (!(p.title ?? "").trim()) {
    blockers.push("Programme title is required");
  }
  if (p.nfqLevel == null) {
    blockers.push("NFQ level is required");
  }
  if (!p.totalCredits) {
    blockers.push("Programme credits (ECTS) are required");
  }
  if ((p.versions ?? []).length === 0) {
    blockers.push("At least one programme version is required");
  }
  if ((p.modules ?? []).length === 0) {
    blockers.push("At least one module is required");
  }
  if ((p.plos ?? []).length === 0) {
    blockers.push("At least one programme learning outcome (PLO) is required");
  }
  if ((p.awardStandardIds ?? []).length === 0 && (p.awardStandardNames ?? []).length === 0) {
    blockers.push("At least one award standard is required");
  }

  const validPloIds = new Set((p.plos ?? []).map((plo) => plo.id));
  const mappedMimloIds = new Set(
    Object.entries(p.ploToMimlos ?? {})
      .filter(([ploId]) => validPloIds.has(ploId))
      .flatMap(([, mimloIds]) => mimloIds ?? []),
  );
  (p.modules ?? []).forEach((module) => {
    const moduleLabel = module.code?.trim() || module.title?.trim() || module.id;
    (module.mimlos ?? []).forEach((mimlo, index) => {
      if (!mappedMimloIds.has(mimlo.id)) {
        blockers.push(`${moduleLabel}: MIMLO ${index + 1} is not mapped to any PLO`);
      }
    });
  });

  if (!isFeAwardType(p.awardType ?? "")) {
    const he = p.heDescriptor ?? {};
    const required: [string | number | undefined, string][] = [
      [he.awardClass, "HE award class is required"],
      [he.iscedCode, "ISCED code is required"],
      [he.submissionDate, "Descriptor submission date is required"],
      [he.approvedHoursPerCredit, "Approved learner effort hours per ECTS are required"],
      [he.programmeSynopsis, "Programme synopsis is required"],
      [he.graduateAttributes, "Graduate attributes are required"],
      [he.rationale, "Programme rationale is required"],
      [
        he.teachingLearningAssessmentStrategy,
        "Teaching, learning and assessment strategy is required",
      ],
      [he.accessRequirements, "Access and entry requirements are required"],
      [he.assessmentIntegrityNarrative, "Assessment integrity narrative is required"],
      [he.learningEnvironmentNarrative, "Programme learning environment is required"],
      [he.psrbStatus, "PSRB status is required"],
    ];
    required.forEach(([value, message]) => {
      if (value == null || String(value).trim() === "" || value === 0) {
        blockers.push(message);
      }
    });
    if (he.psrbStatus && he.psrbStatus !== "not-applicable" && !he.psrbDetails?.trim()) {
      blockers.push("PSRB details are required for the selected status");
    }
    if (he.psrbStatus && he.psrbStatus !== "not-applicable" && !he.psrbName?.trim()) {
      blockers.push("PSRB name is required for the selected status");
    }
    if (he.psrbStatus && he.psrbStatus !== "not-applicable" && !he.psrbWebsite?.trim()) {
      blockers.push("PSRB website is required for the selected status");
    }
    if (he.psrbStatus && he.psrbStatus !== "not-applicable" && !he.psrbApprovalTiming?.trim()) {
      blockers.push("PSRB approval timing is required for the selected status");
    }
    (p.modules ?? []).forEach((mod) => {
      const label = mod.code?.trim() || mod.title?.trim() || mod.id;
      if (!mod.synopsis?.trim()) {
        blockers.push(`${label}: module synopsis is required`);
      }
      if (!mod.indicativeContent?.trim()) {
        blockers.push(`${label}: indicative module content is required`);
      }
    });
  }

  return blockers;
}

/**
 * Build the canonical version-agnostic descriptor data for a programme.
 *
 * Produces the shared {@link DescriptorData} consumed by the variant-specific
 * descriptor mappers. Variant mappers (CAS / non-CAS) call this to obtain the
 * common field mapping and then apply only their variant-specific overrides.
 *
 * @param p - The full programme state
 * @returns DescriptorData ready for docx-templates createReport()
 */
export function buildDescriptorCore(
  programmeInput: Programme,
  context: DescriptorBuildContext = {},
): DescriptorData {
  const p: Programme = context.versionId
    ? projectProgrammeAssessmentVersion(programmeInput, context.versionId)
    : programmeInput;
  const plos: PLO[] = p.plos ?? [];
  const modules: Module[] = p.modules ?? [];
  const versions: ProgrammeVersion[] = p.versions ?? [];
  const ploToMimlos: Record<string, string[]> = p.ploToMimlos ?? {};
  const he = p.heDescriptor ?? {};
  const maximumAnnualEcts = Math.max(
    0,
    ...versions.flatMap((version) =>
      (version.stages ?? [])
        .map((stage) => stage.creditsTarget)
        .filter((credits): credits is number => typeof credits === "number" && credits >= 0),
    ),
  );

  // --- Simple fields ---
  // The QQI descriptor template labels programme outcomes as "MIPLO". Once the
  // outcomes are mapped to award standards we present them under that template
  // label; the internal field remains "plos" (PLO) — only the export label maps.
  const outcomesUseMiploLabel = plos.some((o) => (o.standardMappings?.length ?? 0) > 0);
  // §2.4 lists each outcome as an individually numbered, separated item (PD-084).
  // Every item is separated by a blank line so each renders as its own paragraph,
  // whether or not standard mappings exist — the mapped path uses the QQI "MIPLO"
  // label, the unmapped path falls back to a plain numbered prefix.
  const miplosText = plos.length
    ? plos
        .map((o, i) =>
          outcomesUseMiploLabel ? `MIPLO ${i + 1}: ${o.text ?? ""}` : `${i + 1}. ${o.text ?? ""}`,
        )
        .join("\n\n")
    : "";
  const miploEntries = plos.map((plo, index) => ({
    code: plo.code?.trim() || `MIPLO ${index + 1}`,
    text: plo.text ?? "",
  }));

  const awardStandardName = (p.awardStandardNames ?? [])[0] ?? (p.awardStandardIds ?? [])[0] ?? "";

  // --- Modules ---
  const descriptorModules: DescriptorModule[] = modules.map((mod) => {
    const { stageName, semester, durationWeeks, deliveryKey } = findModuleStage(mod.id, versions);
    const effort = resolveEffortHours(mod, deliveryKey);
    const mimlos = mod.mimlos ?? [];
    const assessments = mod.assessments ?? [];

    // Assessment percentages by category
    const pcts: Record<string, number> = {
      continuous: 0,
      invigilated: 0,
      proctored: 0,
      project: 0,
      practical: 0,
      workBased: 0,
    };
    assessments.forEach((a) => {
      const cat = classifyAssessmentType(a);
      pcts[cat] += a.weighting ?? 0;
    });

    // Effort field mapping
    const effortOnsite = (effort.classroomHours ?? 0) as number;
    const effortSyncOnline = (effort.mentoringHours ?? 0) as number;
    const effortSyncHybrid = (effort.otherContactHours ?? 0) as number;
    const effortAsync = (effort.directedElearningHours ?? 0) as number;
    const effortIndependent = (effort.independentLearningHours ?? 0) as number;
    const effortWorkBased = (effort.workBasedHours ?? 0) as number;
    const effortOther = (effort.otherHours ?? 0) as number;
    const effortTotal =
      effortOnsite +
      effortSyncOnline +
      effortSyncHybrid +
      effortAsync +
      effortIndependent +
      effortWorkBased +
      effortOther;

    const hoursPerWeek =
      effortTotal > 0 && durationWeeks > 0 ? Math.round(effortTotal / durationWeeks) : 0;

    // Reading list
    const readingItems = mod.readingList ?? [];
    const readingListText =
      readingItems
        .map((item) => {
          const core = item.isCore ? "[Core] " : "";
          return `${core}${item.citation ?? item.title ?? ""}`;
        })
        .filter((s) => s.trim())
        .join("\n") || "No reading list entered";

    return {
      code: mod.code ?? "",
      title: mod.title ?? "",
      stage: mod.stage != null ? String(mod.stage) : stageName,
      semester: mod.semester != null ? String(mod.semester) : semester,
      credits: mod.credits ?? 0,
      mandatoryElective: mod.isElective ? "E" : "M",
      durationWeeks: fmtNum(durationWeeks),
      hoursPerWeek: fmtNum(hoursPerWeek),

      effortOnsite: fmtNum(effortOnsite),
      effortSyncOnline: fmtNum(effortSyncOnline),
      effortSyncHybrid: fmtNum(effortSyncHybrid),
      effortAsync: fmtNum(effortAsync),
      effortIndependent: fmtNum(effortIndependent),
      effortWorkBased: fmtNum(effortWorkBased),
      effortOther: fmtNum(effortOther),
      effortOtherDescription: String(
        (effort as unknown as Record<string, unknown>).otherHoursSpecify ?? "",
      ),
      effortTotal: fmtNum(effortTotal),
      effortIncomplete: effortTotal <= 0,

      assessContinuous: fmtPct(pcts.continuous),
      assessExamInPerson: fmtPct(pcts.invigilated),
      assessPractical: fmtPct(pcts.practical),
      assessProject: fmtPct(pcts.project),
      assessExamOnline: fmtPct(pcts.proctored),
      assessWorkBased: fmtPct(pcts.workBased),

      mimlos: mimlos.map((mimlo, i) => ({
        index: i + 1,
        text: mimlo.text ?? "",
        relatedPlos: getRelatedPlos(mimlo.id, plos, ploToMimlos),
      })),

      assessments: assessments.map((a) => {
        const technique = assessmentTechnique(a.type, a.title);
        const requirementLabels = assessmentRequirementLabels(a);
        return {
          mimloText: getAssessmentMimloText(a, mod),
          title: a.title ?? a.type ?? "",
          type: a.type ?? "",
          technique:
            requirementLabels.length > 0
              ? `${technique} (${formatAssessmentRequirements(a)})`
              : technique,
          requirements: formatAssessmentRequirements(a),
          weighting: a.weighting ?? 0,
          notes: a.notes ?? "",
        };
      }),

      readingListText,
      synopsis: mod.synopsis ?? "",
      indicativeContent: mod.indicativeContent ?? "",
      prerequisites: mod.prerequisites ?? "",
      coRequisites: mod.coRequisites ?? "",
      resourcesRequired: mod.resourcesRequired ?? "",
      maximumLearners: mod.maximumLearners != null ? String(mod.maximumLearners) : "",
      staffQualifications: mod.staffQualifications ?? "",
    };
  });

  // --- PLO Assessment Map (Section 6.8) ---
  const ploAssessmentMap: DescriptorPloAssessment[] = plos.map((plo, i) => {
    const mimloIds = ploToMimlos[plo.id] ?? [];

    // Find modules and their MIMLOs that address this PLO
    const moduleMimlos: string[] = [];
    const techniques: Set<string> = new Set();
    const weightings: string[] = [];
    const casComponents: Set<string> = new Set();

    modules.forEach((mod) => {
      const modMimlos = mod.mimlos ?? [];
      const matchingMimlos = modMimlos.filter((m) => mimloIds.includes(m.id));
      if (matchingMimlos.length > 0) {
        const mimloNums = matchingMimlos.map((m) => {
          const idx = modMimlos.indexOf(m);
          return `MIMLO ${idx + 1}`;
        });
        moduleMimlos.push(`${mod.code || mod.title} (${mimloNums.join(", ")})`);
        if (mod.casComponentCode) {
          casComponents.add(`${mod.casComponentCode} ${mod.title ?? ""}`.trim());
        }

        // Find assessments that assess these MIMLOs
        (mod.assessments ?? []).forEach((a) => {
          const asmtMimloIds = a.mimloIds ?? [];
          if (matchingMimlos.some((m) => asmtMimloIds.includes(m.id))) {
            techniques.add(a.type ?? "");
            const label = mod.code || mod.title;
            const detail = [a.type, a.weighting == null ? null : `${a.weighting}%`]
              .filter(Boolean)
              .join(" ");
            weightings.push(detail ? `${label}: ${detail}` : label);
          }
        });
      }
    });

    return {
      index: i + 1,
      moduleMimloText: moduleMimlos.join("; "),
      assessmentTechniques: [...techniques].join(", "),
      weightings: weightings.join("\n"),
      casComponents: [...casComponents].join("\n"),
    };
  });

  // --- Appendix 1 Strand Mappings ---
  const appendixRelationships = buildAppendixRelationships(
    plos,
    ploToMimlos,
    modules,
    (p.awardStandardIds ?? [])[0] ?? "",
  );
  const appendixStrands = buildAppendixStrands(
    appendixRelationships,
    context.awardStandards ?? [],
    Number(p.nfqLevel ?? 0),
  );

  // --- Embedded programme (omitted entirely when none exists) ---
  const embeddedProgrammeSections = buildEmbeddedSections(p);
  const awardPathways = buildAwardPathways(p);
  const electiveChoiceSlots = buildElectiveChoiceSlots(p);

  // --- Schedule section (embedded within the canonical descriptor export) ---
  const scheduleData = buildScheduleData(p);
  const scheduleSections = scheduleData.stages;
  const proposedProgrammeScheduleRows = scheduleSections.flatMap((stage) => stage.modules);
  const programmeIntakes = programmeIntakeMonths(p).join(", ");

  return {
    programme_title: p.title ?? "",
    provider_name: p.providerSettings?.name ?? "",
    provider_address: p.providerSettings?.address ?? "",
    provider_website: p.providerSettings?.website ?? "",
    provider_qa_procedures_url:
      p.providerSettings?.qaProceduresUrl ?? p.providerSettings?.exportMetadata?.qaUrl ?? "",
    validation_contact_name:
      p.providerSettings?.validationContact?.name ?? p.providerSettings?.contactName ?? "",
    validation_contact_title: p.providerSettings?.validationContact?.title ?? "",
    validation_contact_address: p.providerSettings?.validationContact?.address ?? "",
    validation_contact_email:
      p.providerSettings?.validationContact?.email ?? p.providerSettings?.contactEmail ?? "",
    validation_contact_phone: p.providerSettings?.validationContact?.phone ?? "",
    validation_contact_mobile: p.providerSettings?.validationContact?.mobile ?? "",
    programme_manager_name: context.programmeLeadContact?.name ?? "",
    programme_manager_title: context.programmeLeadContact?.title ?? "",
    programme_manager_address: context.programmeLeadContact?.address ?? "",
    programme_manager_email: context.programmeLeadContact?.email ?? "",
    programme_manager_phone: context.programmeLeadContact?.phone ?? "",
    programme_manager_mobile: context.programmeLeadContact?.mobile ?? "",
    document_date: formatDocumentDate(he.submissionDate || p.updatedAt),
    award_class: he.awardClass?.trim() || p.awardType || "",
    award_type: p.awardType ?? "",
    isced_code: he.iscedCode ?? "",
    approved_hours_per_credit:
      he.approvedHoursPerCredit != null ? String(he.approvedHoursPerCredit) : "",
    nfq_level: p.nfqLevel != null ? String(p.nfqLevel) : MANUAL_COMPLETION_REQUIRED,
    principal_programme_nfq_level:
      p.nfqLevel != null ? String(p.nfqLevel) : MANUAL_COMPLETION_REQUIRED,
    ects: String(p.totalCredits ?? ""),
    maximum_annual_ects: maximumAnnualEcts > 0 ? String(maximumAnnualEcts) : "",
    award_standard_name: awardStandardName,
    miplos: miplosText,
    miploEntries,
    principal_programme_duration: versionSummary(versions, (version) => {
      if (typeof version.duration === "string" && version.duration.trim()) {
        return version.duration;
      }
      const weeks = versionTeachingWeeks(version);
      return weeks ? `${weeks} weeks` : "";
    }),
    principal_programme_duration_years: durationYears(versions),
    principal_programme_intakes: versionSummary(versions, (version) => {
      const intakes = version.intakes ?? [];
      return intakes.length > 0 ? intakes.join(", ") : programmeIntakes;
    }),
    principal_programme_cohort: versionSummary(versions, (version) =>
      version.targetCohortSize != null ? String(version.targetCohortSize) : "",
    ),
    assessment_integrity_summary:
      he.assessmentIntegrityNarrative?.trim() || buildIntegritySummary(scheduleSections),
    learning_environment_summary:
      he.learningEnvironmentNarrative?.trim() ||
      versionSummary(versions, (version) =>
        typeof version.deliveryNotes === "string" ? version.deliveryNotes : "",
      ),
    programme_synopsis: he.programmeSynopsis ?? "",
    graduate_attributes: he.graduateAttributes ?? "",
    target_group: he.targetGroup ?? "",
    tla_strategy: he.teachingLearningAssessmentStrategy ?? "",
    access_requirements: he.accessRequirements ?? "",
    rpl_arrangements: he.rplArrangements ?? "",
    transfer_progression: he.transferProgression ?? "",
    inwardTransferArrangements: transferProgressionArrangements(
      he.transferProgressionArrangements,
      "inward-transfer",
    ),
    inwardProgressionArrangements: transferProgressionArrangements(
      he.transferProgressionArrangements,
      "inward-progression",
    ),
    outwardTransferArrangements: transferProgressionArrangements(
      he.transferProgressionArrangements,
      "outward-transfer",
    ),
    outwardProgressionArrangements: transferProgressionArrangements(
      he.transferProgressionArrangements,
      "outward-progression",
    ),
    staffProfiles: staffProfiles(he.staffProfiles),
    resourceEntries: resourceEntries(he.resourceEntries),
    psrb_status: he.psrbStatus ?? "",
    psrb_details: he.psrbDetails ?? "",
    psrb_submission_required: psrbSubmissionRequired(he.psrbStatus),
    psrb_name: he.psrbName ?? "",
    psrb_website: he.psrbWebsite ?? "",
    psrb_approval_timing: he.psrbApprovalTiming ?? "",
    staffing_plan: he.staffingPlan ?? "",
    resource_requirements: he.resourceRequirements ?? "",
    programme_rationale: narrativeSection(he.rationale || p.rationale),
    programmeVersionTicks: buildProgrammeVersionTicks(versions),
    descriptorDeliveryTicks: buildDescriptorDeliveryTicks(versions),
    deliveryModeMatrix: buildDeliveryModeMatrix(versions),
    intake_months: programmeIntakes,

    modules: descriptorModules,
    ploAssessmentMap,
    stageLevelOutline: buildStageLevelOutline(p, versions),
    moduleLevelOutline: buildModuleLevelOutline(modules, versions),
    moduleLevelOutlineStages: buildModuleLevelOutlineStages(modules, versions),
    assessmentCalendar: buildAssessmentCalendar(modules),
    assessmentCalendarTables: buildAssessmentCalendarTables(scheduleSections),
    appendixRelationships,
    appendixStrands,
    assessmentStrategy: buildAssessmentStrategy(modules),
    scheduleSections,
    scheduleData,
    proposedProgrammeScheduleRows,
    embeddedAwardPathways: awardPathways.filter(
      (award) => award.relationship !== "CAS component award",
    ),
    embedded_award_summary: buildEmbeddedAwardSummary(
      awardPathways.filter((award) => award.relationship !== "CAS component award"),
    ),
    embeddedAwardRecords: embeddedProgrammeSections ?? [],
    casComponentAwards: awardPathways.filter(
      (award) => award.relationship === "CAS component award",
    ),
    electiveChoiceSlots,
    elective_choice_summary: buildElectiveChoiceSummary(electiveChoiceSlots),
    representativeProgrammePathway: buildRepresentativeProgrammePathway(p),
    exportBlockers: [
      ...computeExportBlockers(p),
      ...computeAppendixDescriptorBlockers(p, appendixStrands, context),
    ],

    ...(embeddedProgrammeSections ? { embeddedProgrammeSections } : {}),
  };
}
